import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import {
  Stack,
  DrawerBody,
  VStack,
  HStack,
  Text,
  DrawerFooter,
  Button,
  Spinner,
  Textarea,
} from "@chakra-ui/react";
import colors from "app/colors";
import {
  HappycoExportToWorkOrderInput,
  HappycoExportToWorkOrderMutation,
  HappycoExportToWorkOrderMutationVariables,
  HappycoWorkOrderFragment,
  MessageFileInput,
} from "gql/graphql";
import { HAPPYCO_THREAD_DRAFT_ID } from "modules/utils/Constants";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { SingleValue } from "chakra-react-select";
import { useOverlay } from "modules/providers/overlay-provider";
import usePlainText from "modules/hooks/use-plain-text";
import useDisplayToast from "modules/hooks/use-display-toast";
import HappycoWorkOrderSelector from "./happyco-work-order-selector";
import NewMessageUploadAttachmentList from "modules/messaging/_shared/new-message-upload-attachment-list";
import { useMutation } from "@apollo/client";
import HAPPYCO_EXPORT_TO_WORK_ORDER_MUTATION from "./_networking/happyco-export-to-work-order";
import { getMessageFileInput } from "modules/utils/Functions";
import Toast from "modules/toasts/toast";
import { WorkOrderIcon } from "modules/icons";

const HappycoExportToExistingWorkOrderForm = () => {
  const { closeDrawer } = useOverlay();
  const { displayToast } = useDisplayToast();
  const { draft, files, deleteDraft } = useMessageThreadDraft({
    draftId: HAPPYCO_THREAD_DRAFT_ID,
  });

  const [happycoExportToWorkOrderAPI, { loading }] = useMutation<
    HappycoExportToWorkOrderMutation,
    HappycoExportToWorkOrderMutationVariables
  >(HAPPYCO_EXPORT_TO_WORK_ORDER_MUTATION);

  const [comment, setComment] = useState<string | undefined>();
  const [happycoWorkOrderId, setHappycoWorkOrderId] = useState<
    string | undefined
  >();

  const { getPlainText } = usePlainText();

  useEffect(() => {
    setComment((prev) => {
      if (prev) {
        return prev;
      }
      return getPlainText(draft?.text ?? "");
    });
  }, [draft?.text, getPlainText]);

  const onCommentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setComment(e.target.value);
    },
    [],
  );

  const happycoExportToWorkOrderInput = useMemo(
    (): HappycoExportToWorkOrderInput | undefined =>
      happycoWorkOrderId
        ? {
            happycoWorkOrderId,
            comment,
            fileList: files?.map(
              (file): MessageFileInput => getMessageFileInput(file),
            ),
          }
        : undefined,
    [comment, files, happycoWorkOrderId],
  );

  const onExportToWorkOrderClicked = useCallback(async () => {
    if (!happycoExportToWorkOrderInput) return;
    await happycoExportToWorkOrderAPI({
      variables: {
        input: happycoExportToWorkOrderInput,
      },
    });

    displayToast(() => (
      <Toast
        message="Comment and/or files sent to HappyCo"
        leftIcon={<WorkOrderIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
    deleteDraft();
    closeDrawer();
  }, [
    deleteDraft,
    closeDrawer,
    displayToast,
    happycoExportToWorkOrderAPI,
    happycoExportToWorkOrderInput,
  ]);

  const onWorkOrderSelected = useCallback(
    (happycoWorkOrder?: SingleValue<HappycoWorkOrderFragment>) => {
      setHappycoWorkOrderId(happycoWorkOrder?.happycoId);
    },
    [],
  );

  return (
    <>
      <DrawerBody px={3} pt={6}>
        <Stack w="full" h="full" spacing={6} flex={1}>
          <VStack w="full" alignItems="flex-start">
            <Text fontWeight={600} fontSize={14} color={colors.blackSecondary}>
              Select Work Order
            </Text>
            <HappycoWorkOrderSelector
              onWorkOrderSelected={onWorkOrderSelected}
            />
          </VStack>

          <VStack
            borderRadius={8}
            borderColor={colors.grayPrimary}
            borderWidth={1}
            flex={1}
            w="full"
          >
            <Textarea
              placeholder="Enter description..."
              fontSize={16}
              color={colors.blackSecondary}
              fontWeight={500}
              border="none"
              w="full"
              outline="none"
              boxShadow="none"
              _hover={{
                border: "none",
              }}
              _focus={{
                border: "none",
                outline: "none",
                boxShadow: "none",
              }}
              _focusVisible={{
                border: "none",
                outline: "none",
                boxShadow: "none",
              }}
              minH={110}
              resize="none"
              flex={1}
              onChange={onCommentChange}
              value={comment}
            />
            <NewMessageUploadAttachmentList
              draftId={HAPPYCO_THREAD_DRAFT_ID}
              files={files}
            />
          </VStack>
        </Stack>
      </DrawerBody>

      <DrawerFooter h="96px">
        <HStack w="full" justifyContent="center" spacing={8}>
          <Button
            variant="hover"
            bg={colors.white}
            color={colors.gray40}
            borderColor={colors.gray40}
            borderWidth={1}
            fontSize={16}
            fontWeight={700}
            onClick={closeDrawer}
            borderRadius={4}
            w={180}
            size="md"
          >
            Cancel
          </Button>
          <Button
            variant="hover"
            bg={colors.gray40}
            color={colors.white}
            fontSize={16}
            fontWeight={700}
            onClick={onExportToWorkOrderClicked}
            isDisabled={!happycoWorkOrderId || loading}
            borderRadius={4}
            w={180}
            size="md"
          >
            {loading ? <Spinner /> : "Send"}
          </Button>
        </HStack>
      </DrawerFooter>
    </>
  );
};

export default memo(HappycoExportToExistingWorkOrderForm);
